import { normalizeBookedShuttle } from "./normalizeBookedShuttle";
import { shuttleDateMatch } from "./shuttleTravelDate";
import { tripsStillHeld } from "../../Lib/Utils/booking";
import { mapBookedShuttlesToSideLists } from "../../Lib/Utils/shuttleStops";
import { isShuttleOffer, AdmissionStatusEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { OWNED_ADMISSION_STATUSES } from "./hasPendingExchange";
import { IBooking, ILegTrain } from "../../Orchestrator/Types/Index";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleAddedJourney } from "../../Orchestrator/Types/Interface/IShuttleBus";
import { ITrip } from "../../Orchestrator/Types/Interface/ITrip";
import { IBookedOffer } from "../../Orchestrator/Types/Interface/IOffer";

/** Admission states meaning the passenger no longer holds the travel right. */
const TERMINAL = new Set<string>([
  AdmissionStatusEnum.REFUNDED,
  AdmissionStatusEnum.RELEASED,
  AdmissionStatusEnum.EXCHANGED,
]);

/**
 * Booked shuttles for ONE leg, derived straight from the booking.
 *
 * `shuttleAddedAtom` is keyed by side alone, so on a round trip the outbound's
 * `toStation` and the inbound's `toStation` collide and hydration's merge drops
 * one — it then renders on neither row and cannot be cancelled. Re-keying that
 * atom means touching 28 references across the checkout and legacy-drawer
 * paths, which are the verified-working parts of this feature; deriving per leg
 * here fixes the surface where the collision actually bites, using the same
 * booking-as-source-of-truth pattern as [[bookedNolForLeg]].
 */
export function bookedShuttlesForLeg(
  booking: IBooking | undefined,
  trainLeg: ILegTrain | undefined,
  /** Offers THIS session refunded — see refundedShuttleOfferIdsAtom. Their
      admissions stay non-terminal until the payment session settles, so
      without this a refund followed by a re-book redisplays the old shuttle. */
  refundedOfferIds: string[] = [],
  opts: {
    /**
     * Keep only offers the passenger has PAID for, dropping PREBOOKED/ON_HOLD.
     *
     * Must be exercised HERE rather than by the caller filtering the result,
     * because `mapBookedShuttlesToSides` collapses a side to one offer with
     * last-in-array winning and says in its own comment that the caller has to
     * exclude the losers first. Filtering afterwards let an add abandoned at
     * payment — appended after the paid offer — win the side and then fail the
     * caller's ownership check, so a shuttle the passenger had paid for went
     * invisible and unrefundable while the UI offered to sell them another.
     *
     * Opt-in rather than always-on, matching `bookedNolForLeg`'s `paidOnly`.
     * Every production caller passes it today; what the default protects is the
     * documented no-admissions/non-terminal behaviour that
     * `refundedShuttleExclusion.test.ts` pins — including a PREBOOKED offer
     * surviving, which a basket-style caller would need.
     */
    ownedOnly?: boolean;
    /** See the list form. Collapsing a cancelled side is legal but unused. */
    cancelledOnly?: boolean;
  } = {}
): Partial<Record<RideHailingMode, IShuttleAddedJourney>> {
  const lists = bookedShuttleListsForLeg(booking, trainLeg, refundedOfferIds, opts);
  const result: Partial<Record<RideHailingMode, IShuttleAddedJourney>> = {};
  // Last-in-array wins, exactly as mapBookedShuttlesToSides has always done —
  // see its comment for why nothing in the payload can rank two offers.
  (Object.keys(lists) as RideHailingMode[]).forEach((side) => {
    const entries = lists[side] || [];
    if (entries.length > 0) result[side] = entries[entries.length - 1];
  });
  return result;
}

/**
 * Whether one booked offer counts as a shuttle this leg holds.
 *
 * Shared by both forms so the collapsed and list views cannot disagree about
 * what is owned.
 */
function keepShuttleOffer(
  offer: NonNullable<IBooking["bookedOffers"]>[number],
  refunded: Set<string>,
  ownedOnly?: boolean,
  cancelledOnly?: boolean
): boolean {
  // Decided on the offer's ADMISSION product — a train offer carrying a
  // shuttle ANCILLARY_ITEM (e.g. SHTL-MBZ-REEM) is not a shuttle offer.
  // See isShuttleOffer.
  if (!isShuttleOffer(offer)) return false;
  if (cancelledOnly) {
    // The exact complement of the owned view, and the only place
    // `refundedOfferIds` counts FOR an offer rather than against it: a refund
    // settled this session leaves the admissions reading FULFILLED for a
    // moment, and the ticket must move to the cancelled group in that window
    // rather than disappear until the booking catches up.
    if (refunded.has(offer?.offerId || "")) return true;
    return (offer?.admissions || []).some(
      (a) => a?.status === AdmissionStatusEnum.REFUNDED
    );
  }
  if (refunded.has(offer?.offerId || "")) return false;
  // An offer whose admissions are ALL terminal is history, not a booking.
  // No admissions at all proves nothing, so keep it.
  const admissions = offer?.admissions || [];
  if (ownedOnly) {
    // Deliberately stricter than the no-admissions rule below: "proves
    // nothing" cannot satisfy "is paid for", and a caller asking for owned
    // offers only is choosing to show nothing over showing an unpaid one.
    return admissions.some((a) => OWNED_ADMISSION_STATUSES.has(a?.status || ""));
  }
  if (admissions.length === 0) return true;
  return admissions.some((a) => !TERMINAL.has(a?.status || ""));
}

/**
 * ALL booked shuttles for one leg, per side — [[bookedShuttlesForLeg]] without
 * the collapse to one offer.
 *
 * A side holds more than one once a rider left out of an earlier shuttle books
 * their own (see [[mapBookedShuttlesToSideLists]]). Every surface that DRAWS or
 * REFUNDS a shuttle must read this, because the collapsed form silently drops
 * the losers — and a dropped offer is one the passenger paid for and can no
 * longer see or cancel.
 *
 * Same filters, same order, same options as the collapsed form: this is the
 * source both now come from, so they cannot disagree about what is owned.
 */
export function bookedShuttleListsForLeg(
  booking: IBooking | undefined,
  trainLeg: ILegTrain | undefined,
  refundedOfferIds: string[] = [],
  opts: { ownedOnly?: boolean; cancelledOnly?: boolean } = {}
): Partial<Record<RideHailingMode, IShuttleAddedJourney[]>> {
  if (!booking || !trainLeg) return {};
  const refunded = new Set(refundedOfferIds);

  const tripsById = new Map<string, ITrip>();
  (booking.trips || []).forEach((t) => {
    if (t?.id) tripsById.set(t.id, t as unknown as ITrip);
  });

  const shuttleOffers = (booking.bookedOffers || []).filter((o) =>
    keepShuttleOffer(o, refunded, opts.ownedOnly, opts.cancelledOnly)
  );

  const normalized = (shuttleOffers as unknown as IBookedOffer[]).map((o) => {
    // A refunded offer has no held admissions, so the default normalisation
    // reads it as an empty shuttle and the cancelled group would draw nothing.
    // See normalizeBookedShuttle's `admissions` option.
    //
    // Asked per offer, not per call: a refund settled THIS session is in the
    // cancelled list on `refundedOfferIds` alone, while its admissions still
    // read FULFILLED — normalising that one as "terminal" would empty the very
    // card the refund is supposed to move.
    const hasTerminal = (o.admissions || []).some((a) => TERMINAL.has(a?.status || ""));
    return normalizeBookedShuttle(o, tripsById, booking.passengers || [], {
      admissions: opts.cancelledOnly && hasTerminal ? "terminal" : "held",
    });
  });
  return mapBookedShuttlesToSideLists(
    normalized,
    trainLeg.origin?.stopPlaceRef || "",
    trainLeg.destination?.stopPlaceRef || "",
    (s, side) => !isShuttleFromAnotherLegDate(booking, trainLeg, s, side)
  );
}

/** Minimal shape `trainDateForSide` reads — a sibling trip stood up as a leg. */
type LegDates = { startTime?: string; endTime?: string };

/**
 * True when this trip has been exchanged away and nobody is left travelling on
 * it — at least one admission is EXCHANGE_ONGOING, and every other admission is
 * either EXCHANGE_ONGOING too or already terminal.
 *
 * NOT simply `every(EXCHANGE_ONGOING)`. After a committed PARTIAL change-date
 * the original offer reads ["EXCHANGED", "FULFILLED"]; when the remaining
 * passenger later starts their OWN change-date it reads
 * ["EXCHANGED", "EXCHANGE_ONGOING"]. Nobody is travelling on that trip, but
 * `every` is false — and `tripsStillHeld` keeps it too, since not ALL of its
 * admissions are terminal. The trip would then hold the old date again and the
 * bug this helper exists to kill would come straight back on the second
 * sequential exchange. Terminal admissions are ghosts; only a LIVE non-moving
 * rider may keep the trip's claim.
 *
 * EVERY live rider, not some: change-date is per-passenger. If part of the
 * party stays behind on the original date, that trip is genuinely still
 * travelled and its claim on the shuttle is real — it must keep outranking the
 * mover's leg, or the stayers' shuttle becomes cancellable from the mover's
 * card (FPNNN8, a paid ticket destroyed from the wrong surface).
 *
 * The whole TERMINAL set counts as ghosts, not just EXCHANGED — which the
 * second-sequential-exchange case above argues for on its own. REFUNDED and
 * RELEASED are deliberate too: on `["REFUNDED", "EXCHANGE_ONGOING"]` (a
 * co-traveller cancelled their train, then someone else change-dates) the trip
 * is dropped and that co-traveller's orphaned shuttle surfaces on the mover's
 * card. That is the intended trade. Once the exchange commits, the mover's card
 * is the ONLY surface left that can show and refund the orphan — and
 * ManageEndToEndRow keeps owned products refundable through a pending exchange
 * precisely so it can be — so stranding it invisibly is the worse error by this
 * file's own stated policy (see keepShuttleOffer).
 *
 * Deliberately NOT folded into `tripsStillHeld`. Its other caller is the
 * bookings LIST (GetBookingToRightDataAction), where a trip under an
 * uncommitted exchange must keep showing: dropping it there would flip the row
 * to the new, UNPAID date. The narrower question — "may this trip's date
 * outrank the leg I am rendering?" — belongs here.
 *
 * Answers false when no covering offer contributes any admission at all.
 * NOTE the tension: for THIS guard, inert means HIDE, which is the opposite of
 * the preference its neighbours document (`isShuttleFromAnotherLegDate` and
 * `isShuttleOfferOnOtherLeg` both refuse to convict on unknown). Chosen anyway
 * because the failure modes are not symmetric — hiding a shuttle is
 * obstructive, while wrongly attributing one is destructive (FPNNN8). Barely
 * reachable in practice, and for a stronger reason than "different trip": the
 * offers documented to carry no admissions array are NOL offers, which have no
 * offer-level `tripCoverage` AT ALL (only their admission names a leg — see
 * bookedNolForLeg). So `coveredTripId === tripId` is never true for one, and
 * they cannot reach this aggregate however the trip is shaped.
 */
const isTripFullyExchangedAway = (booking: IBooking, tripId: string | undefined): boolean => {
  if (!tripId) return false;
  const admissions = (booking?.bookedOffers || [])
    .filter((offer) => offer?.tripCoverage?.coveredTripId === tripId)
    .flatMap((offer) => offer?.admissions || []);
  if (admissions.length === 0) return false;
  if (!admissions.some((a) => a?.status === AdmissionStatusEnum.EXCHANGE_ONGOING)) return false;
  return !admissions.some(
    (a) => a?.status !== AdmissionStatusEnum.EXCHANGE_ONGOING && !TERMINAL.has(a?.status || ""),
  );
};

/**
 * The booking's OTHER trips running the SAME route as `trainLeg`.
 *
 * Same route is the whole point: a shuttle is placed by endpoint refs, so only
 * a trip sharing those refs could have had its shuttle mistaken for this leg's.
 * It also keeps shuttle trips out without a mode check — theirs run stop <->
 * station, never station <-> station.
 *
 * ponytail: two ceilings, both fail the same safe way — the guard goes inert
 * and the leak survives, but nothing is ever wrongly hidden.
 *   1. DAY granularity. A change-date onto a different train on the SAME date
 *      leaves both cards answering "match", so the leak is untouched. Compare
 *      the shuttle's depDateTime against the leg's time window instead of the
 *      calendar day if that case ever needs covering.
 *   2. The LEG's endpoints are compared against TRIP-level ones, so on a
 *      journey with a transfer no leg finds a sibling. Compare per-leg if a
 *      multi-leg route ever ships with shuttles.
 */
const siblingTripsOnRoute = (booking: IBooking, trainLeg: ILegTrain): LegDates[] => {
  const origin = trainLeg.origin?.stopPlaceRef || "";
  const destination = trainLeg.destination?.stopPlaceRef || "";
  if (!origin || !destination) return [];
  // tripsStillHeld, NOT booking.trips: a change-date does not remove the trip
  // it moved the passenger OFF — it leaves it behind (see Lib/Utils/booking).
  // NOT "the same filter" as shuttleAddRequest/BookingOptions, whatever an
  // earlier version of this comment claimed: those route through
  // getAllOfferWithoutExchange, which is EXCHANGED-only and narrower. Counted
  // here, that dead trip claims the date of the shuttle a FULL change-date
  // stranded, and the card would hide a shuttle the passenger still owns and
  // can still refund. Only a trip someone is actually travelling on can
  // outrank this leg.
  return tripsStillHeld(booking.trips || [], booking.bookedOffers || [])
    // ...but `tripsStillHeld` alone does NOT achieve that while the exchange is
    // still UNCOMMITTED, and this is where the rule above used to break. See
    // isTripFullyExchangedAway for why the fix lives here and not in there.
    //
    // Its terminal set is ["EXCHANGED","REFUNDED","RELEASED"]. A committed
    // change-date leaves the old trip EXCHANGED, so it is correctly dropped.
    // But between "new train chosen" and "paid", the superseded trip sits at
    // EXCHANGE_ONGOING — NOT terminal — so it stayed "still held", kept its old
    // date, and matched the stranded shuttle. `isShuttleFromAnotherLegDate`
    // then judged the passenger's own shuttle to belong to another leg, so
    // `ownedShuttle` went false, `hasAnyOnwardRow` went false, and
    // OnwardTravelCard returned null: the whole End-to-end section vanished
    // from the change-date checkout, notice included. Verified on INT booking
    // 947362f9 (#YLX7CX) — shuttle PCH->PMB FULFILLED, old trip EXCHANGE_ONGOING.
    //
    // EVERY admission, not some: a change-date is per-passenger. If part of the
    // party stays behind, the old trip is still genuinely travelled on that
    // date and its claim on the shuttle is real — so it must keep outranking
    // this leg. Only a trip nobody is left travelling on is excluded.
    .filter((t) => !isTripFullyExchangedAway(booking, t?.id))
    .filter((t) => {
      const legs = t?.legs || [];
      // The trip this leg belongs to is not a sibling. Failing to recognise it
      // is harmless in both directions — its dates ARE this leg's, so it can
      // only ever answer "mismatch" where trainLeg already did, never "match".
      // What would break the rule is two trips sharing a leg id: the real
      // sibling gets excluded and the leak comes back silently.
      if (legs.some((l) => l?.id === trainLeg.legId)) return false;
      const endpoints = tripEndpoints(t);
      return endpoints.origin === origin && endpoints.destination === destination;
    })
    .map((t) => {
      const legs = t?.legs || [];
      return {
        startTime: legs[0]?.timedLeg?.start?.serviceDeparture?.timetabledTime,
        endTime: legs[legs.length - 1]?.timedLeg?.end?.serviceArrival?.timetabledTime,
      };
    });
};

/** Trip-level refs, falling back to the first/last leg — the same rule
    normalizeBookedShuttle uses, so both sides of the comparison agree. */
const tripEndpoints = (trip: ITrip): { origin: string; destination: string } => {
  const legs = trip?.legs || [];
  return {
    origin:
      trip?.origin?.stopPlaceRef || legs[0]?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "",
    destination:
      trip?.destination?.stopPlaceRef ||
      legs[legs.length - 1]?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef ||
      "",
  };
};

/**
 * PROOF that a shuttle belongs to a DIFFERENT trip of this booking than
 * `trainLeg` — the date twin of [[isShuttleOfferOnOtherLeg]], for the case that
 * predicate cannot see: two trips on the same route, where the refs match both.
 *
 * A partial change-date creates exactly that. The passengers who stayed keep
 * the original trip, the mover gets a new one on the same route, and the side
 * mapping — which compares stop refs alone — handed every card every shuttle.
 * Reported on FPNNN8: the moved passenger's card advertised "2 shuttle tickets"
 * that belonged to the party left behind, and its Manage End-to-end row offered
 * to cancel and refund them.
 *
 * A day mismatch on its own is NOT proof, and must never be treated as such: a
 * FULL change-date moves the only trip and strands its shuttle on the old date,
 * where the card is supposed to keep drawing it under the "wrong day" warning
 * (shuttleDateMatch) so the customer can refund it. The extra thing asked here
 * is that some OTHER same-route trip claims that day — then, and only then, the
 * shuttle has a better owner than this leg.
 */
export function isShuttleFromAnotherLegDate(
  booking: IBooking | undefined,
  trainLeg: ILegTrain | undefined,
  entry: { depDateTime?: string | null } | null | undefined,
  side: RideHailingMode
): boolean {
  if (!booking || !trainLeg || !entry) return false;
  // "unknown" included: an unreadable date cannot convict. Losing a paid
  // shuttle over a parse failure is the worse of the two errors.
  if (shuttleDateMatch(entry, trainLeg, side) !== "mismatch") return false;
  return siblingTripsOnRoute(booking, trainLeg).some(
    (sibling) => shuttleDateMatch(entry, sibling as unknown as ILegTrain, side) === "match"
  );
}

/**
 * PROOF that a shuttle offer belongs to a leg other than `trainLeg` — the
 * inverse of the side mapping above, asked about one offer.
 *
 * `shuttleAddedAtom` holds one entry per SIDE for the whole app, so on a round
 * trip the outbound's "fromStation" and the inbound's are the same slot. Every
 * consumer that reads that atom to decide what a leg already owns can therefore
 * be handed the other direction's shuttle — and in confirmOnwardSelection that
 * meant DELETING it (measured on preprod 2026-08-11: adding an outbound shuttle
 * removed the inbound's, with no error shown).
 *
 * Deliberately answers "no" unless the booking can place the offer: an offer
 * that is absent, unreadable, or whose covering trip is missing is UNKNOWN, not
 * foreign, and the callers' existing money guards (which refuse rather than
 * destroy on unknown) must keep seeing it.
 */
export function isShuttleOfferOnOtherLeg(
  booking: IBooking | undefined,
  trainLeg: ILegTrain | undefined,
  offerId: string | undefined
): boolean {
  const origin = trainLeg?.origin?.stopPlaceRef || "";
  const destination = trainLeg?.destination?.stopPlaceRef || "";
  if (!booking || !offerId || !origin || !destination) return false;

  const offer = (booking.bookedOffers || []).find(
    (o) => (o as { offerId?: string })?.offerId === offerId
  );
  if (!offer || !isShuttleOffer(offer)) return false;

  const trip = (booking.trips || []).find(
    (t) => t?.id === (offer as unknown as IBookedOffer)?.tripCoverage?.coveredTripId
  );
  if (!trip) return false;

  // Only two of its fields are used, and it does name lookups and price math
  // for neither — but the rule for WHERE a shuttle's endpoint refs come from
  // (trip-level, falling back to the first/last leg) has to stay identical to
  // the one bookedShuttlesForLeg above uses, or this predicate and the side
  // mapping could disagree about the same offer. One source, one rule.
  const normalized = normalizeBookedShuttle(
    offer as unknown as IBookedOffer,
    new Map<string, ITrip>([[trip.id, trip as unknown as ITrip]])
  );
  // The two ways a shuttle can belong to THIS leg — it ends where the train
  // starts, or starts where the train ends. Neither means another leg's.
  return normalized.destinationRef !== origin && normalized.originRef !== destination;
}
